// Quantra API client with Firebase authentication
import { auth } from './firebase';
import { normalizeQuantraRequestForApi } from './api-normalizers';

const API_BASE_URL = import.meta.env.VITE_QUANTRA_API_URL || 'https://api.quantra.io';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeLegacyEndpointRequest(endpoint: string, body: unknown): unknown {
  if (!isRecord(body)) return body;

  if (endpoint === '/bootstrap-curves' && !('pricing' in body)) {
    const {
      queries,
      ...pricing
    } = body;

    if (
      'as_of_date' in pricing ||
      'settlement_date' in pricing ||
      'indices' in pricing ||
      'swap_indices' in pricing ||
      'curves' in pricing ||
      'coupon_pricers' in pricing ||
      'quotes' in pricing ||
      'credit_curves' in pricing ||
      'vol_surfaces' in pricing ||
      'models' in pricing ||
      'equity_underlyings' in pricing ||
      'inflation_indices' in pricing ||
      'inflation_curves' in pricing
    ) {
      return {
        ...(queries !== undefined ? { queries } : {}),
        pricing,
      };
    }
  }

  return body;
}

function normalizeDateSeparators(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeDateSeparators);
  }
  if (typeof value === 'string') {
    // Backend DateToQL parser expects %Y/%m/%d in many request fields.
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.replace(/-/g, '/') : value;
  }
  if (!value || typeof value !== 'object') return value;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = normalizeDateSeparators(v);
  }
  return out;
}

// ============================================================================
// Types
// ============================================================================

// Structured error info from the JSON server. The wire payload for failing
// gRPC calls looks like:
//   { "error": "gRPC error", "code": 10, "code_name": "ABORTED",
//     "message": "Swaption vol '...' references unknown swap_index_id: ..." }
// We keep the full picture so the UI can show category/HTTP status and a
// "raw response" details block alongside the friendly message.
export interface PricingErrorInfo {
  message: string;
  httpStatus: number;
  code?: number;
  codeName?: string;
  category: 'validation' | 'not_found' | 'unavailable' | 'auth' | 'server' | 'network' | 'client';
  raw?: unknown;
}

export interface PricingResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  errorInfo?: PricingErrorInfo;
  duration_ms: number;
}

// Map a gRPC code_name (or, as a fallback, an HTTP status) into the broad
// category we use to drive icon/color/title in the UI.
function categorizeError(codeName: string | undefined, httpStatus: number): PricingErrorInfo['category'] {
  switch (codeName) {
    case 'INVALID_ARGUMENT':
    case 'FAILED_PRECONDITION':
    case 'OUT_OF_RANGE':
      return 'validation';
    case 'NOT_FOUND':
      return 'not_found';
    case 'UNAVAILABLE':
    case 'DEADLINE_EXCEEDED':
    case 'RESOURCE_EXHAUSTED':
      return 'unavailable';
    case 'UNAUTHENTICATED':
    case 'PERMISSION_DENIED':
      return 'auth';
    case 'INTERNAL':
    case 'UNKNOWN':
    case 'DATA_LOSS':
    case 'ABORTED':
    case 'CANCELLED':
      return 'server';
    default:
      break;
  }
  if (httpStatus === 400 || httpStatus === 422) return 'validation';
  if (httpStatus === 404) return 'not_found';
  if (httpStatus === 401 || httpStatus === 403) return 'auth';
  if (httpStatus === 408 || httpStatus === 429 || httpStatus === 503 || httpStatus === 504) return 'unavailable';
  if (httpStatus >= 500) return 'server';
  return 'client';
}

export interface SwapResult {
  npv: number;
  fair_rate: number;
  fixed_leg_bps: number;
  floating_leg_bps: number;
}

export interface OisSwapResult {
  npv?: number;
  fair_rate?: number;
  fair_spread?: number;
  fixed_leg_bps?: number;
  overnight_leg_bps?: number;
  fixed_leg_npv?: number;
  overnight_leg_npv?: number;
  fixed_leg_flows?: Array<Record<string, unknown>>;
  overnight_leg_flows?: Array<Record<string, unknown>>;
}

export interface BasisSwapResult {
  npv?: number;
  fair_spread_leg1?: number;
  fair_spread_leg2?: number;
  leg1_bps?: number;
  leg2_bps?: number;
  leg1_npv?: number;
  leg2_npv?: number;
  leg1_flows?: Array<Record<string, unknown>>;
  leg2_flows?: Array<Record<string, unknown>>;
}

export interface ZeroCouponInflationSwapResult {
  npv?: number;
  fair_rate?: number;
  fixed_leg_bps?: number;
  fixed_leg_npv?: number;
  inflation_leg_npv?: number;
  fixed_leg_flows?: Array<Record<string, unknown>>;
  inflation_leg_flows?: Array<Record<string, unknown>>;
}

export interface YearOnYearInflationSwapResult {
  npv?: number;
  fair_rate?: number;
  fair_spread?: number;
  fixed_leg_bps?: number;
  yoy_leg_bps?: number;
  fixed_leg_npv?: number;
  yoy_leg_npv?: number;
  fixed_leg_flows?: Array<Record<string, unknown>>;
  yoy_leg_flows?: Array<Record<string, unknown>>;
}

export interface BondResult {
  npv: number;
  clean_price: number;
  dirty_price: number;
  accrued_amount: number;
  yield: number;
  duration: number;
  convexity: number;
}

export interface EquityOptionResult {
  npv?: number;
  implied_vol?: number;
  implied_volatility?: number;
  delta?: number;
  gamma?: number;
  vega?: number;
  theta?: number;
  rho?: number;
  engine_used?: string;
  used_spot?: number;
  used_strike?: number;
  used_settlement?: string;
  error?: { error_message?: string };
}

// Bootstrap Curves Response types
export interface CurveSeries {
  measure: 'DF' | 'ZERO' | 'FWD' | 'ZeroRate' | 'YoYRate' | string;
  values: number[];
}

export interface BootstrapCurveResult {
  id: string;
  reference_date: string;
  grid_dates: string[];
  series: CurveSeries[];
  pillar_dates?: string[];
  error?: { error_message: string };
}

export interface BootstrapCurvesResponse {
  results: BootstrapCurveResult[];
}

export interface VolSurfaceSampleResult {
  vol_id: string;
  reference_date?: string;
  ql_vol_type?: string;
  requested_strike_axis?: 'AbsoluteStrike' | 'SpreadFromATM';
  canonical_strike_kind?: string;
  allow_extrapolation_used?: boolean;
  calendar_used?: string;
  business_day_convention_used?: string;
  expiry_kind?: string;
  expiries?: string[];
  requested_expiry_grid_points?: string[];
  tenors?: Array<{ n: number; unit: string }>;
  effective_swap_starts?: string[];
  effective_swap_ends?: string[];
  strikes?: number[];
  vols?: number[];
  n_expiries?: number;
  n_tenors?: number;
  n_strikes?: number;
  atm_levels?: number[];
  error?: { error_message?: string };
}

export interface SampleVolSurfacesResponse {
  results: VolSurfaceSampleResult[];
}

export interface CalibrateSwaptionModelResult {
  model_id: string;
  hw_a?: number;
  hw_sigma?: number;
  rmse?: number;
  num_helpers?: number;
  grid_rows?: number;
  grid_cols?: number;
  grid_points?: number;
}

/**
 * Calibration-specific diagnostics (quantra_SabrCalibrationDiagnostics).
 * Populated only for SabrCalibrate surfaces; absent for SabrParams.
 */
export interface SabrCalibrationDiagnostics {
  per_node_rmse?: number[];
  per_node_max_abs_error?: number[];
  overall_rmse?: number;
  converged?: boolean;
  iterations_per_node?: number[];
  strikes?: number[];
  per_strike_fit_error?: number[];
}

/**
 * Diagnostics block describing one swaption vol surface
 * (quantra_SwaptionVolDiagnostics). Emitted by /sample-vol-surfaces and
 * /price-swaption when include_diagnostics=true and as the response payload
 * of /calibrate-swaption-vol.
 */
export interface SwaptionVolDiagnostics {
  vol_id: string;
  kind?: string;
  expiries?: Array<{ n: number; unit: string }>;
  tenors?: Array<{ n: number; unit: string }>;
  n_expiries?: number;
  n_tenors?: number;
  forward_per_node?: number[];
  atm_vol_per_node?: number[];
  time_to_expiry_per_node?: number[];
  alpha_per_node?: number[];
  beta_per_node?: number[];
  rho_per_node?: number[];
  nu_per_node?: number[];
  calibration?: SabrCalibrationDiagnostics;
  warnings?: string[];
}

/**
 * Response from POST /calibrate-swaption-vol — a thin envelope around a
 * single SwaptionVolDiagnostics block. Mirrors the shape of
 * CalibrateSwaptionModelResponse for Hull-White (vol_id + diagnostics
 * instead of model_id + parameters).
 */
export interface CalibrateSwaptionVolResponse {
  vol_id: string;
  diagnostics: SwaptionVolDiagnostics;
}

export const CALENDAR_NAMES = [
  'Argentina',
  'Australia',
  'BespokeCalendar',
  'Brazil',
  'Canada',
  'China',
  'CzechRepublic',
  'Denmark',
  'Finland',
  'Germany',
  'HongKong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Israel',
  'Italy',
  'Japan',
  'Mexico',
  'NewZealand',
  'Norway',
  'NullCalendar',
  'Poland',
  'Romania',
  'Russia',
  'SaudiArabia',
  'Singapore',
  'Slovakia',
  'SouthAfrica',
  'SouthKorea',
  'Sweden',
  'Switzerland',
  'TARGET',
  'Taiwan',
  'Turkey',
  'Ukraine',
  'UnitedKingdom',
  'UnitedStates',
  'UnitedStatesGovernmentBond',
  'UnitedStatesNERC',
  'UnitedStatesNYSE',
  'UnitedStatesSettlement',
  'WeekendsOnly',
] as const;

export type CalendarName = (typeof CALENDAR_NAMES)[number];

export interface CalendarBusinessDaysRequest {
  calendar?: CalendarName;
  start_date: string;
  end_date: string;
  include_start?: boolean;
  include_end?: boolean;
}

export interface CalendarBusinessDaysResponse {
  calendar?: CalendarName;
  start_date?: string;
  end_date?: string;
  dates: string[];
  count?: number;
}

export interface CalendarHolidaysRequest {
  calendar?: CalendarName;
  start_date: string;
  end_date: string;
  include_weekends?: boolean;
}

export interface CalendarHolidaysResponse {
  calendar?: CalendarName;
  start_date?: string;
  end_date?: string;
  dates: string[];
  count?: number;
}

// ============================================================================
// Auth Helper
// ============================================================================

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated. Please sign in.');
  }

  try {
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  } catch (error) {
    console.error('Failed to get auth token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
}

// ============================================================================
// API Request Helper
// ============================================================================

async function apiRequest<T>(endpoint: string, body: unknown, baseUrl: string = API_BASE_URL): Promise<PricingResult<T>> {
  const startTime = performance.now();

  try {
    const headers = await getAuthHeaders();
    const normalizedBody = normalizeDateSeparators(
      normalizeQuantraRequestForApi(normalizeLegacyEndpointRequest(endpoint, body))
    );

    const send = (payload: unknown) => fetch(`${baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    let response = await send(normalizedBody);

    const duration_ms = performance.now() - startTime;

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let raw: unknown;
      let code: number | undefined;
      let codeName: string | undefined;

      try {
        const errorData = await response.json();
        raw = errorData;
        if (errorData && typeof errorData === 'object') {
          const record = errorData as Record<string, unknown>;
          if (typeof record.message === 'string' && record.message.length > 0) {
            errorMessage = record.message;
          } else if (typeof record.error === 'string' && record.error.length > 0) {
            errorMessage = record.error;
          }
          if (typeof record.code === 'number') code = record.code;
          if (typeof record.code_name === 'string') codeName = record.code_name;
        }
      } catch {
        // Body wasn't JSON; keep the HTTP-derived message.
      }

      const errorInfo: PricingErrorInfo = {
        message: errorMessage,
        httpStatus: response.status,
        code,
        codeName,
        category: categorizeError(codeName, response.status),
        raw,
      };

      return {
        success: false,
        error: errorMessage,
        errorInfo,
        duration_ms,
      };
    }

    const data = await response.json();

    return {
      success: true,
      data,
      duration_ms,
    };
  } catch (error) {
    const duration_ms = performance.now() - startTime;

    if (error instanceof TypeError && error.message.includes('fetch')) {
      const message = 'Network error. Check your connection.';
      return {
        success: false,
        error: message,
        errorInfo: {
          message,
          httpStatus: 0,
          category: 'network',
          raw: error.message,
        },
        duration_ms,
      };
    }

    const message = error instanceof Error ? error.message : 'Unknown error';
    const isAuthError = message.startsWith('Not authenticated') || message.startsWith('Authentication failed');
    return {
      success: false,
      error: message,
      errorInfo: {
        message,
        httpStatus: isAuthError ? 401 : 0,
        category: isAuthError ? 'auth' : 'client',
        raw: error instanceof Error ? { name: error.name, message: error.message } : error,
      },
      duration_ms,
    };
  }
}

function normalizeEquityOptionResult(raw: unknown): EquityOptionResult {
  if (!raw || typeof raw !== 'object') return {};
  const value = raw as Record<string, unknown>;
  return {
    ...(value as EquityOptionResult),
    implied_vol:
      typeof value.implied_vol === 'number'
        ? value.implied_vol
        : typeof value.implied_volatility === 'number'
          ? value.implied_volatility
          : undefined,
  };
}

// ============================================================================
// Product Pricing Functions
// ============================================================================

export const quantraApi = {
  // Fixed Rate Bond
  priceFixedRateBond: (request: unknown) =>
    apiRequest<{ bonds: BondResult[] }>('/price-fixed-rate-bond', request),

  // Floating Rate Bond
  priceFloatingRateBond: (request: unknown) =>
    apiRequest<{ bonds: BondResult[] }>('/price-floating-rate-bond', request),

  // Vanilla Swap
  priceVanillaSwap: (request: unknown) =>
    apiRequest<{ swaps: SwapResult[] }>('/price-vanilla-swap', request),

  // OIS Swap
  priceOisSwap: (request: unknown) =>
    apiRequest<{ swaps: OisSwapResult[] }>('/price-ois-swap', request),

  // Basis Swap
  priceBasisSwap: (request: unknown) =>
    apiRequest<{ swaps: BasisSwapResult[] }>('/price-basis-swap', request),

  // Zero Coupon Inflation Swap
  priceZeroCouponInflationSwap: (request: unknown) =>
    apiRequest<{ swaps: ZeroCouponInflationSwapResult[] }>('/price-zero-coupon-inflation-swap', request),

  // Year-on-Year Inflation Swap
  priceYearOnYearInflationSwap: (request: unknown) =>
    apiRequest<{ swaps: YearOnYearInflationSwapResult[] }>('/price-year-on-year-inflation-swap', request),

  // FRA
  priceFRA: (request: unknown) =>
    apiRequest<{ fras: { npv: number; forward_rate: number }[] }>('/price-fra', request),

  // Cap/Floor
  priceCapFloor: (request: unknown) =>
    apiRequest<{ cap_floors: { npv: number; atm_rate: number }[] }>('/price-cap-floor', request),

  // Swaption
  priceSwaption: (request: unknown) =>
    apiRequest<{ swaptions: { npv: number }[] }>('/price-swaption', request),

  // Swaption model calibration (Hull-White lattice)
  calibrateSwaptionModel: (request: unknown) =>
    apiRequest<CalibrateSwaptionModelResult>('/calibrate-swaption-model', request),

  // Swaption SABR vol surface calibration. The request must reference a
  // SwaptionSabrCalibrateSpec surface inside `pricing.volatility.vol_surfaces`
  // and supply explicit discounting/forwarding curve ids (the endpoint stands
  // alone, no trade-side curve fallback like /price-swaption has).
  // Errors map: 400 (QuantraInvalidArgument), 404 (QuantraNotFound) — both
  // surface through the shared apiRequest error pipeline as PricingErrorInfo.
  calibrateSwaptionVol: (request: unknown) =>
    apiRequest<CalibrateSwaptionVolResponse>('/calibrate-swaption-vol', request),

  // CDS
  priceCDS: (request: unknown) =>
    apiRequest<{ cds_list: { npv: number; fair_spread: number; fair_upfront?: number; default_leg_npv?: number; premium_leg_npv?: number; error?: { error_message?: string } }[] }>('/price-cds', request),

  // Equity options
  priceEquityOptions: async (request: unknown) => {
    const response = await apiRequest<{ options?: EquityOptionResult[] } | EquityOptionResult>('/price-equity-option', request);
    if (!response.success || !response.data) {
      return response as PricingResult<{ options: EquityOptionResult[] }>;
    }

    const rawData = response.data as { options?: EquityOptionResult[] } | EquityOptionResult;
    const rawOptions = Array.isArray((rawData as { options?: EquityOptionResult[] }).options)
      ? (rawData as { options: EquityOptionResult[] }).options
      : [rawData as EquityOptionResult];

    return {
      ...response,
      data: {
        options: rawOptions.map(normalizeEquityOptionResult),
      },
    };
  },

  // Bootstrap Curves - returns discount factors, zero rates, forward rates
  bootstrapCurves: (request: unknown) =>
    apiRequest<BootstrapCurvesResponse>('/bootstrap-curves', request),

  // Bootstrap Inflation Curves - returns zero or YoY inflation term structures
  bootstrapInflationCurves: (request: unknown) =>
    apiRequest<BootstrapCurvesResponse>('/bootstrap-inflation-curves', request),

  // Vol Surface Sampling
  sampleVolSurfaces: (request: unknown) =>
    apiRequest<SampleVolSurfacesResponse>('/sample-vol-surfaces', request),

  // Calendar utilities
  calendarBusinessDays: (request: CalendarBusinessDaysRequest) =>
    apiRequest<CalendarBusinessDaysResponse>('/calendar-business-days', request),
  calendarHolidays: (request: CalendarHolidaysRequest) =>
    apiRequest<CalendarHolidaysResponse>('/calendar-holidays', request),

  // Generic pricing (auto-detect endpoint)
  price: (endpoint: string, request: unknown) =>
    apiRequest(endpoint, request),

  // Health check (no auth needed)
  health: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      return await response.json();
    } catch {
      return { status: 'unreachable' };
    }
  },

  // Verify current auth status
  verifyAuth: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/auth/me`, { headers });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  },
};

// ============================================================================
// Product Type Definitions
// ============================================================================

export const PRODUCT_TYPES = [
  {
    id: 'vanilla-swap',
    name: 'Vanilla Swap',
    endpoint: '/price-vanilla-swap',
    description: 'Fixed-for-floating interest rate swap'
  },
  {
    id: 'ois-swap',
    name: 'OIS Swap',
    endpoint: '/price-ois-swap',
    description: 'Fixed-for-overnight indexed swap'
  },
  {
    id: 'basis-swap',
    name: 'Basis Swap',
    endpoint: '/price-basis-swap',
    description: 'Two-floating-leg basis swap'
  },
  {
    id: 'zero-coupon-inflation-swap',
    name: 'Zero Coupon Inflation Swap',
    endpoint: '/price-zero-coupon-inflation-swap',
    description: 'Fixed-vs-inflation zero-coupon swap'
  },
  {
    id: 'year-on-year-inflation-swap',
    name: 'Year-on-Year Inflation Swap',
    endpoint: '/price-year-on-year-inflation-swap',
    description: 'Coupon-paying year-on-year inflation swap'
  },
  {
    id: 'fixed-rate-bond',
    name: 'Fixed Rate Bond',
    endpoint: '/price-fixed-rate-bond',
    description: 'Bond with fixed coupon payments'
  },
  {
    id: 'floating-rate-bond',
    name: 'Floating Rate Bond',
    endpoint: '/price-floating-rate-bond',
    description: 'Bond with floating rate coupons'
  },
  {
    id: 'fra',
    name: 'FRA',
    endpoint: '/price-fra',
    description: 'Forward Rate Agreement'
  },
  {
    id: 'cap-floor',
    name: 'Cap/Floor',
    endpoint: '/price-cap-floor',
    description: 'Interest rate cap or floor'
  },
  {
    id: 'swaption',
    name: 'Swaption',
    endpoint: '/price-swaption',
    description: 'Option on interest rate swap'
  },
  {
    id: 'cds',
    name: 'CDS',
    endpoint: '/price-cds',
    description: 'Credit default swap'
  },
  {
    id: 'equity-option',
    name: 'Equity Option',
    endpoint: '/price-equity-option',
    description: 'Vanilla equity option'
  },
] as const;

export type ProductType = typeof PRODUCT_TYPES[number]['id'];

export function getProductByEndpoint(endpoint: string) {
  return PRODUCT_TYPES.find(p => p.endpoint === endpoint);
}